import { startDownloadInFrame as startDownload } from '@/api/rpc/downloadUrl';
import { useCallback, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ProjectEntry } from '@erp/rpc';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DownloadSimpleIcon,
  FileIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { DateTime, T, Var, useGT } from 'gt-react';
import { orpc } from '@/api/rpc/client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatMarkdownStyles } from '@/components/common/chat';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { Button } from '@/components/ui/button';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { formatFileSize } from '@/lib/pricelist-utils';

const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;

interface ProjectFilePreviewDialogProps {
  projectId: string;
  entry: ProjectEntry | null;
  files: ProjectEntry[];
  onEntryChange: (entry: ProjectEntry) => void;
  onClose: () => void;
  onAskNami?: (entry: ProjectEntry) => void;
}

function TextPreview({ url, entry }: { url: string; entry: ProjectEntry }) {
  const gt = useGT();
  const text = useQuery({
    queryKey: ['project-file-text-preview', url],
    queryFn: async ({ signal }) => {
      const response = await fetch(url, { signal });
      if (!response.ok) {
        throw new Error(
          gt('This file preview could not be loaded.', {
            context: 'project text file preview load error',
          }),
        );
      }
      return response.text();
    },
    retry: false,
  });
  if (text.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (text.error) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-destructive">
        {text.error.message}
      </div>
    );
  }
  const isMarkdown =
    entry.mimeType === 'text/markdown' ||
    entry.name.toLowerCase().endsWith('.md');
  return (
    <ScrollArea className="h-full min-h-0 bg-background">
      <div className="mx-auto max-w-4xl p-6">
        {isMarkdown ? (
          <div className={chatMarkdownStyles}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {text.data}
            </ReactMarkdown>
          </div>
        ) : (
          <pre className="whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-foreground">
            {text.data}
          </pre>
        )}
      </div>
    </ScrollArea>
  );
}

export function ImagePreview({ url, name }: { url: string; name: string }) {
  const gt = useGT();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const reset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (zoom <= 1) return;
      event.preventDefault();
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        panX: pan.x,
        panY: pan.y,
      };
      const move = (moveEvent: MouseEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        setPan({
          x: drag.panX + moveEvent.clientX - drag.startX,
          y: drag.panY + moveEvent.clientY - drag.startY,
        });
      };
      const stop = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', stop);
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', stop);
    },
    [pan, zoom],
  );
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-center gap-1 border-b p-2">
        <Button
          variant="outline"
          size="icon"
          aria-label={gt('Zoom out', {
            context: 'zoom out project image preview button',
          })}
          onClick={() =>
            setZoom((value) => Math.max(MIN_ZOOM, value - ZOOM_STEP))
          }
        >
          <MagnifyingGlassMinusIcon />
        </Button>
        <Button variant="outline" size="sm" onClick={reset}>
          {Math.round(zoom * 100)}%
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label={gt('Zoom in', {
            context: 'zoom in project image preview button',
          })}
          onClick={() =>
            setZoom((value) => Math.min(MAX_ZOOM, value + ZOOM_STEP))
          }
        >
          <MagnifyingGlassPlusIcon />
        </Button>
        {zoom !== 1 && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={gt('Reset zoom', {
              context: 'reset project image preview zoom button',
            })}
            onClick={reset}
          >
            <ArrowCounterClockwiseIcon />
          </Button>
        )}
      </div>
      <div
        className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted/20 p-4"
        role="region"
        aria-label={name}
        tabIndex={0}
        onKeyDown={(event) => {
          const offsets: Record<string, { x: number; y: number }> = {
            ArrowLeft: { x: 30, y: 0 },
            ArrowRight: { x: -30, y: 0 },
            ArrowUp: { x: 0, y: 30 },
            ArrowDown: { x: 0, y: -30 },
          };
          const offset = offsets[event.key];
          if (zoom <= 1 || !offset) return;
          event.preventDefault();
          event.stopPropagation();
          setPan((current) => ({
            x: current.x + offset.x,
            y: current.y + offset.y,
          }));
        }}
        onMouseDown={handleMouseDown}
        onWheel={(event) => {
          if (!event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          setZoom((value) =>
            event.deltaY < 0
              ? Math.min(MAX_ZOOM, value + ZOOM_STEP)
              : Math.max(MIN_ZOOM, value - ZOOM_STEP),
          );
        }}
      >
        <img
          src={url}
          alt={name}
          draggable={false}
          className="max-h-full max-w-full select-none object-contain"
          style={{
            transform: `scale(${String(zoom)}) translate(${String(pan.x / zoom)}px, ${String(pan.y / zoom)}px)`,
            transformOrigin: 'center',
            cursor: zoom > 1 ? 'grab' : 'default',
          }}
        />
      </div>
    </div>
  );
}

function PreviewBody({
  entry,
  preview,
  refresh,
  download,
}: {
  entry: ProjectEntry;
  preview: {
    mode: 'pdf' | 'image' | 'video' | 'audio' | 'text' | 'download';
    url: string;
  };
  refresh: () => void;
  download: () => void;
}) {
  if (preview.mode === 'pdf')
    return (
      <PDFViewer url={preview.url} onUrlExpired={refresh} className="h-full" />
    );
  if (preview.mode === 'image')
    return <ImagePreview key={entry.id} url={preview.url} name={entry.name} />;
  if (preview.mode === 'video')
    return (
      <div className="flex h-full items-center justify-center bg-muted/20 p-4">
        <video src={preview.url} controls className="max-h-full max-w-full" />
      </div>
    );
  if (preview.mode === 'audio')
    return (
      <div className="flex h-full items-center justify-center p-8">
        <audio src={preview.url} controls className="w-full max-w-2xl" />
      </div>
    );
  if (preview.mode === 'text')
    return <TextPreview url={preview.url} entry={entry} />;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <FileIcon className="size-12 text-muted-foreground" />
      <div className="flex flex-col gap-1">
        <p className="font-medium">{entry.name}</p>
        <p className="text-sm text-muted-foreground">
          <T context="unsupported project file preview message">
            This file type cannot be previewed in the browser.
          </T>
        </p>
      </div>
      <Button onClick={download}>
        <DownloadSimpleIcon data-icon="inline-start" />
        <T context="download unsupported project file button">Download file</T>
      </Button>
    </div>
  );
}

// Preview states and file-type controls stay together so their UI conditions are visible.
// fallow-ignore-next-line complexity
export function ProjectFilePreviewDialog({
  projectId,
  entry,
  files,
  onEntryChange,
  onClose,
  onAskNami,
}: ProjectFilePreviewDialogProps) {
  const gt = useGT();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<{
    entryId: string;
    message: string;
  } | null>(null);
  const preview = useQuery({
    ...orpc.projects.files.getPreview.queryOptions({
      input: { projectId, entryId: entry?.id ?? projectId },
    }),
    enabled: entry !== null,
  });
  const currentIndex = entry
    ? files.findIndex((candidate) => candidate.id === entry.id)
    : -1;
  const previous = currentIndex > 0 ? files.at(currentIndex - 1) : undefined;
  const next =
    currentIndex >= 0 && currentIndex < files.length - 1
      ? files.at(currentIndex + 1)
      : undefined;

  const download = async () => {
    if (!entry || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const result = await orpc.projects.files.getDownload.call({
        projectId,
        entryId: entry.id,
      });
      startDownload(result.url);
    } catch (error) {
      setDownloadError({
        entryId: entry.id,
        message:
          error instanceof Error
            ? error.message
            : gt('The download could not be started.', {
                context: 'project file download error',
              }),
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={entry !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-0 p-0 sm:max-w-6xl"
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft' && previous) onEntryChange(previous);
          if (event.key === 'ArrowRight' && next) onEntryChange(next);
        }}
      >
        <DialogHeader className="shrink-0 border-b px-5 py-3 pr-12">
          <div className="flex items-center gap-3">
            <DialogTitle
              className="min-w-0 flex-1 truncate"
              title={entry?.name}
            >
              {entry?.name ?? ''}
            </DialogTitle>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                disabled={!previous}
                aria-label={gt('Previous file', {
                  context: 'previous project file preview button',
                })}
                onClick={() => previous && onEntryChange(previous)}
              >
                <ArrowLeftIcon />
              </Button>
              <span className="min-w-16 text-center text-xs tabular-nums text-muted-foreground">
                {currentIndex >= 0 ? (
                  <T context="position within project file preview list">
                    <Var>{currentIndex + 1}</Var> of <Var>{files.length}</Var>
                  </T>
                ) : null}
              </span>
              <Button
                variant="ghost"
                size="icon"
                disabled={!next}
                aria-label={gt('Next file', {
                  context: 'next project file preview button',
                })}
                onClick={() => next && onEntryChange(next)}
              >
                <ArrowRightIcon />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void download()}
              >
                {downloading ? (
                  <Spinner size="sm" />
                ) : (
                  <DownloadSimpleIcon data-icon="inline-start" />
                )}
                <T context="download project file preview button">Download</T>
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 pt-3">
            <span
              className="min-w-0 truncate text-xs text-muted-foreground"
              title={entry?.path}
            >
              {entry?.path}
            </span>
            {entry && onAskNami && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAskNami(entry)}
              >
                <T context="ask the assistant about the preview file">
                  Ask the assistant about this file
                </T>
              </Button>
            )}
          </div>
          <DialogDescription className="sr-only">
            <T context="project file preview dialog description">
              Preview the selected project file and move between nearby files.
            </T>
          </DialogDescription>
        </DialogHeader>
        {entry && downloadError?.entryId === entry.id && (
          <AlertContainer variant="destructive" className="mx-5 mt-3">
            <AlertDescription>{downloadError.message}</AlertDescription>
          </AlertContainer>
        )}
        <div className="min-h-0 flex-1 overflow-hidden">
          {preview.isPending && (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          )}
          {preview.error && (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-destructive">
                {preview.error.message}
              </p>
              <Button variant="outline" onClick={() => void preview.refetch()}>
                <T context="retry project file preview button">Try again</T>
              </Button>
            </div>
          )}
          {entry && preview.data && (
            <PreviewBody
              key={entry.id}
              entry={entry}
              preview={preview.data}
              refresh={() => void preview.refetch()}
              download={() => void download()}
            />
          )}
        </div>
        {entry && (
          <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-t px-5 py-2 text-xs text-muted-foreground">
            <span>{formatFileSize(entry.sizeBytes ?? 0)}</span>
            <span aria-hidden="true">·</span>
            <T context="project file preview updater metadata">
              Updated by <Var>{entry.updatedBy.name}</Var>
            </T>
            <span aria-hidden="true">·</span>
            <DateTime options={{ dateStyle: 'medium', timeStyle: 'short' }}>
              {entry.updatedAt}
            </DateTime>
            {entry.currentVersionNumber && (
              <>
                <span aria-hidden="true">·</span>
                <T context="project file preview version metadata">
                  Version <Var>{entry.currentVersionNumber}</Var>
                </T>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
